import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { LoadingView } from '../../../common/views/LoadingView';
import { usageCounts } from '../../footage/footageView';
import { useMusicVideoEditor } from '../../MusicVideoEditorContext';
import { footageMessages } from '../../messages/footageMessages';
import { formatTime } from '../../timeline/timelineGeometry';
import { ClipCard } from './ClipCard';
import { useAddClip } from './useAddClip';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The Yours tab (design c1): the song's own music video first (its sound is not used), the clips
 * of this song (uploads and AI clips, the working ones with a shimmer), then the clips of the other
 * songs of the account behind "Show N". A failed AI clip shows in the AI tab only.
 */
const YoursTabComponent: React.FC = () => {
  const intl = useIntl();
  const { footage, doc, catalogItemUuid } = useMusicVideoEditor();
  const { add } = useAddClip();
  const [showOthers, setShowOthers] = useState(false);
  const uses = useMemo(() => usageCounts(doc.clips), [doc.clips]);
  const groups = useMemo(() => {
    const live = footage.own.filter((view) => view.status !== 'failed');
    return {
      songVideo: live.find((view) => view.origin === 'songVideo' && view.catalogItemUuid === catalogItemUuid) ?? null,
      thisSong: live.filter((view) => view.origin !== 'songVideo' && view.catalogItemUuid === catalogItemUuid),
      others: live.filter((view) => view.catalogItemUuid !== catalogItemUuid),
    };
  }, [catalogItemUuid, footage.own]);
  const aiCount = useMemo(() => groups.thisSong.filter((view) => view.origin === 'ai').length, [groups.thisSong]);

  if (footage.isOwnLoading) {
    return <LoadingView />;
  }
  if (footage.ownError !== undefined && footage.own.length === 0) {
    return <ErrorView error={footage.ownError} retry={footage.retryOwn} />;
  }
  return (
    <>
      {groups.songVideo !== null && (
        <>
          <div className={styles.sectionHead}>
            <b>{intl.formatMessage(footageMessages.songVideoHeading)}</b>
            {groups.songVideo.durationSec !== null && (
              <small>
                {intl.formatMessage(footageMessages.songVideoFacts, {
                  length: formatTime(groups.songVideo.durationSec),
                })}
              </small>
            )}
          </div>
          <div className={styles.songVideoRow}>
            <ClipCard view={groups.songVideo} uses={uses.get(groups.songVideo.key) ?? 0} onPick={add} />
            <div className={styles.column}>
              <b>{groups.songVideo.label}</b>
              <p className={styles.hint}>{intl.formatMessage(footageMessages.songVideoHint)}</p>
            </div>
          </div>
        </>
      )}
      <div className={styles.sectionHead}>
        <b>{intl.formatMessage(footageMessages.thisSong)}</b>
        <small>
          {intl.formatMessage(footageMessages.thisSongFacts, {
            uploads: groups.thisSong.length - aiCount,
            ai: aiCount,
          })}
        </small>
      </div>
      {groups.thisSong.length === 0 ? (
        <p className={styles.hint}>{intl.formatMessage(footageMessages.yoursEmpty)}</p>
      ) : (
        <div className={styles.clipGrid}>
          {groups.thisSong.map((view) => (
            <ClipCard key={view.key} view={view} uses={uses.get(view.key) ?? 0} showSource onPick={add} />
          ))}
        </div>
      )}
      {groups.others.length > 0 && (
        <div className={styles.sectionHead}>
          <b>{intl.formatMessage(footageMessages.otherSongs)}</b>
          <span className={styles.grow} />
          {!showOthers && (
            <button type="button" className={styles.linkButton} onClick={() => setShowOthers(true)}>
              {intl.formatMessage(footageMessages.showMore, { count: groups.others.length })}
            </button>
          )}
        </div>
      )}
      {showOthers && (
        <div className={styles.clipGrid}>
          {groups.others.map((view) => (
            <ClipCard key={view.key} view={view} uses={uses.get(view.key) ?? 0} showSource onPick={add} />
          ))}
        </div>
      )}
      <span className={styles.grow} />
      <p className={styles.hint}>{intl.formatMessage(footageMessages.yoursHint)}</p>
    </>
  );
};

export const YoursTab = TypedMemo(YoursTabComponent);
